import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProfileModal from './ProfileModal';
import SettingsModal from './SettingsModal';
import ClubChangeModal from './ClubChangeModal';
import ConvertToCustomerModal from './ConvertToCustomerModal';
import axios from 'axios';
import { trackEvent, trackFeatureUsage } from '../utils/analytics';
import { forceRefreshApp } from '../utils/pwa';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const { user, logout, isOnline } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState(undefined);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showClubChangeModal, setShowClubChangeModal] = useState(false);
  const [showConvertToCustomerModal, setShowConvertToCustomerModal] = useState(false);
  const [appDownloadLoading, setAppDownloadLoading] = useState(false);
  const [appDownloadProgress, setAppDownloadProgress] = useState(0);
  const [appDownloadMessage, setAppDownloadMessage] = useState('');
  const menuRef = useRef(null);
  const languageMenuRef = useRef(null);
  const notificationRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const isEditor = user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR');
  const isCustomer = user?.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER');
  const isCustomerOnly = isCustomer && !isEditor;
  const languageOptions = [
    { code: 'en', label: t('English') },
    { code: 'mr', label: t('Marathi') },
    { code: 'hi', label: t('Hindi') }
  ];
  const selectedLanguageLabel = languageOptions.find((option) => option.code === language)?.label || t('English');

  // Close menu when clicking outside
  useEffect(() => {
    if (!user) return; // Don't set up event listener if no user
    
    const handleClickOutside = (event) => {
      const isInsideMobileMenu = mobileMenuRef.current && mobileMenuRef.current.contains(event.target);

      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (!isInsideMobileMenu && languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [user]);

  // Fetch notifications and unread count
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Refresh unread count every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    setShowUserMenu(false);
    setShowLanguageMenu(false);
    setShowNotifications(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get('/api/notifications/unread-count');
      setUnreadCount(response.data);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    if (notificationsLoading) return;
    
    setNotificationsLoading(true);
    try {
      const response = await axios.get('/api/notifications/unread');
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Track notification interaction
      trackEvent('notification_clicked', {
        event_category: 'Notifications',
        event_label: notification.title,
        notification_type: notification.relatedEntityType || 'general'
      });

      // Mark as read
      await axios.put(`/api/notifications/${notification.id}/read`);
      
      // Update local state
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      
      // Navigate to action URL with correct tab based on notification type
      if (notification.actionUrl) {
        setShowNotifications(false);
        
        // Determine the correct tab based on notification type
        let targetUrl = notification.actionUrl;
        
        if (notification.relatedEntityType === 'CLUB_CHANGE_REQUEST') {
          targetUrl = '/pending-requests?tab=clubchange';
        } else if (notification.relatedEntityType === 'PROMOTION_REQUEST') {
          // Check if it's a conversion request (coach to customer)
          if (notification.message && notification.message.toLowerCase().includes('convert')) {
            targetUrl = '/pending-requests?tab=conversion';
          } else {
            targetUrl = '/pending-requests?tab=promotion';
          }
        } else if (notification.actionUrl.includes('/pending-requests')) {
          // Default to promotion tab if no specific type detected
          targetUrl = '/pending-requests?tab=promotion';
        }
        
        navigate(targetUrl);
      }
    } catch (error) {
      console.error('Failed to handle notification click:', error);
    }
  };

  // Don't show navbar on login page or public invoice view
  if (!user || location.pathname.startsWith('/invoices/')) {
    return null;
  }

  const isActive = (path) => location.pathname === path ? 'active' : '';

  const handleLogout = () => {
    trackEvent('logout_clicked', {
      event_category: 'Authentication',
      event_label: 'User initiated logout'
    });
    setShowUserMenu(false);
    setShowLanguageMenu(false);
    setIsMobileMenuOpen(false);
    logout();
  };

  const handleProfileClick = () => {
    trackFeatureUsage('Profile Modal', 'open');
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
    setShowProfileModal(true);
  };

  const handleNotificationsClick = () => {
    trackFeatureUsage('Notifications', showNotifications ? 'close' : 'open');
    setShowUserMenu(false);
    setShowLanguageMenu(false);
    if (!showNotifications) {
      fetchNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  const handleSettingsClick = (initialTab) => {
    trackFeatureUsage('Settings Modal', 'open');
    setShowUserMenu(false);
    setShowLanguageMenu(false);
    setIsMobileMenuOpen(false);
    setSettingsInitialTab(initialTab);
    setShowSettingsModal(true);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setShowUserMenu(false);
    setShowLanguageMenu(false);
    setShowNotifications(false);
  };

  const handleLanguageChange = (nextLanguage) => {
    setLanguage(nextLanguage);
    setShowLanguageMenu(false);
  };

  const handleLanguageSelect = (event, nextLanguage) => {
    event.preventDefault();
    event.stopPropagation();
    handleLanguageChange(nextLanguage);
  };

  const handleAndroidAppDownload = async () => {
    try {
      setAppDownloadLoading(true);
      setAppDownloadProgress(0);
      setAppDownloadMessage(t('Preparing Android app download...'));
      setShowUserMenu(false);
      setIsMobileMenuOpen(false);

      trackEvent('android_app_download_started', {
        event_category: 'Download',
        event_label: 'nutritions-club.apk'
      });

      const response = await axios.get('/api/app-downloads/android', {
        responseType: 'blob',
        timeout: 0,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setAppDownloadProgress(percent);
            setAppDownloadMessage(t('Downloading Android app... {percent}%', { percent }));
          } else {
            setAppDownloadMessage(t('Downloading Android app...'));
          }
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'nutritions-club.apk');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      trackEvent('android_app_download_success', {
        event_category: 'Download',
        event_label: 'nutritions-club.apk'
      });
      setAppDownloadProgress(100);
      setAppDownloadMessage(t('Android app download started.'));
      window.setTimeout(() => {
        setAppDownloadLoading(false);
        setAppDownloadProgress(0);
        setAppDownloadMessage('');
      }, 4000);
    } catch (error) {
      console.error('Failed to download Android app APK:', error);
      trackEvent('android_app_download_failed', {
        event_category: 'Download',
        event_label: 'nutritions-club.apk'
      });
      setAppDownloadLoading(false);
      setAppDownloadProgress(0);
      setAppDownloadMessage(
        error?.code === 'ECONNABORTED'
          ? t('Android app download timed out. Please try again.')
          : t('Failed to download Android app. Please try again.')
      );
    }
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-content">
          <Link to="/dashboard" className="navbar-brand">
            Calorie N Sweat
            {!isOnline && (
              <span style={{
                marginLeft: '8px',
                fontSize: '12px',
                background: 'var(--error)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '10px'
              }}>
                {t('Offline')}
              </span>
            )}
          </Link>
          
          {/* Desktop Navigation */}
          <ul className="navbar-nav desktop-nav">
            <li>
              <Link to="/dashboard" className={isActive('/dashboard')}>
                <span className="nav-icon">📊</span>
                <span className="nav-text">{t('Dashboard')}</span>
              </Link>
            </li>
            {!user.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER') && (
              <>
                {!user.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                  <li>
                    <Link to="/hierarchy" className={isActive('/hierarchy')}>
                      <span className="nav-icon">🌐</span>
                      <span className="nav-text">{t('Network')}</span>
                    </Link>
                  </li>
                )}
                {user.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                  <>
                    <li>
                      <Link to="/leads" className={isActive('/leads')}>
                        <span className="nav-icon">📋</span>
                        <span className="nav-text">{t('Leads')}</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/learning-center" className={isActive('/learning-center')}>
                        <span className="nav-icon">📚</span>
                        <span className="nav-text">{t('Learning Center')}</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/platform-tour" className={isActive('/platform-tour')}>
                        <span className="nav-icon">🎥</span>
                        <span className="nav-text">{t('Platform Tour')}</span>
                      </Link>
                    </li>
                  </>
                )}
                <li>
                  <Link to="/about-us" className={isActive('/about-us')}>
                    <span className="nav-icon">ℹ️</span>
                    <span className="nav-text">{t('About Us')}</span>
                  </Link>
                </li>
              </>
            )}
            {user.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER') && (
              <li>
                <Link to="/platform-tour" className={isActive('/platform-tour')}>
                  <span className="nav-icon">🎥</span>
                  <span className="nav-text">{t('Platform Tour')}</span>
                </Link>
              </li>
            )}
            <li ref={languageMenuRef} className={`nav-dropdown ${showLanguageMenu ? 'open' : ''}`}>
              <button
                type="button"
                className={`nav-dropdown-trigger ${showLanguageMenu ? 'active' : ''}`}
                onClick={() => setShowLanguageMenu((prev) => !prev)}
                aria-expanded={showLanguageMenu}
                aria-haspopup="true"
              >
                <span className="nav-icon">🌐</span>
                <span className="nav-text">{t('Language')}: {selectedLanguageLabel}</span>
                <span className="nav-dropdown-arrow">▼</span>
              </button>
              {showLanguageMenu && (
                <div className="nav-dropdown-menu">
                  {languageOptions.map((option) => (
                    <button
                      key={option.code}
                      type="button"
                      className={`menu-item ${language === option.code ? 'selected' : ''}`}
                      onMouseDown={(event) => handleLanguageSelect(event, option.code)}
                      onClick={(event) => handleLanguageSelect(event, option.code)}
                    >
                      <span className="menu-icon">{language === option.code ? '✓' : '•'}</span>
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </li>
          </ul>

          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label={t('Toggle mobile menu')}
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          <div className="user-info desktop-user-info">
            {/* Notifications */}
            <div ref={notificationRef} style={{ position: 'relative' }}>
              <button 
                onClick={handleNotificationsClick}
                className="user-menu-btn"
                style={{ position: 'relative' }}
              >
                <span className="user-avatar">🔔</span>
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    background: 'var(--error)',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              
              {showNotifications && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  background: 'var(--white)',
                  border: '1px solid var(--gray-medium)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                  minWidth: '350px',
                  maxWidth: '400px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  marginTop: '5px'
                }}>
                  <div style={{
                    padding: '15px',
                    borderBottom: '1px solid var(--gray-medium)',
                    background: 'var(--pale-blue)',
                    fontWeight: 'bold',
                    color: 'var(--dark-blue)'
                  }}>
                    {t('Notifications ({count} unread)', { count: unreadCount })}
                  </div>
                  
                  {notificationsLoading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--gray-dark)' }}>
                      {t('Loading notifications...')}
                    </div>
                  ) : notifications.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--gray-dark)' }}>
                      {t('No new notifications')}
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        style={{
                          padding: '12px 15px',
                          borderBottom: '1px solid var(--gray-light)',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <div style={{ 
                          fontWeight: '500', 
                          color: 'var(--dark-blue)',
                          marginBottom: '4px',
                          fontSize: '14px'
                        }}>
                          {notification.title}
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: 'var(--gray-dark)',
                          marginBottom: '4px'
                        }}>
                          {notification.message}
                        </div>
                        <div style={{ 
                          fontSize: '10px', 
                          color: 'var(--gray-medium)'
                        }}>
                          {new Date(notification.createdAt).toLocaleString()}
                        </div>
                      </div>
                    ))
                  )}
                  
                  {notifications.length > 0 && (
                    <div style={{
                      padding: '10px 15px',
                      textAlign: 'center',
                      borderTop: '1px solid var(--gray-medium)',
                      background: 'var(--gray-light)'
                    }}>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put('/api/notifications/mark-all-read');
                            setUnreadCount(0);
                            setNotifications([]);
                            setShowNotifications(false);
                          } catch (error) {
                            console.error('Failed to mark all as read:', error);
                          }
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--primary-blue)',
                          cursor: 'pointer',
                          fontSize: '12px',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('Mark all as read')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div ref={menuRef}>
              <span className="user-name">{t('Hi, {name}', { name: user.firstName || user.email })}</span>
              <div className="user-menu-container">
                <button 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="user-menu-btn"
                >
                  <span className="user-avatar">👤</span>
                  <span className="dropdown-arrow">▼</span>
                </button>
              
                {showUserMenu && (
                  <div className="user-menu-dropdown">
                    <button onClick={handleProfileClick} className="menu-item">
                      <span className="menu-icon">👤</span>
                      {t('My Profile')}
                    </button>
                    {isCustomerOnly && (
                      <>
                        <button onClick={() => navigate('/attendance/scan')} className="menu-item">
                          <span className="menu-icon">📋</span>
                          {t('Scan Attendance QR')}
                        </button>
                        <button onClick={handleAndroidAppDownload} className="menu-item">
                          <span className="menu-icon">📱</span>
                          {t('Android App')}
                        </button>
                        <button onClick={() => handleSettingsClick('attendance')} className="menu-item">
                          <span className="menu-icon">⚙️</span>
                          {t('Settings')}
                        </button>
                      </>
                    )}
                    {isEditor && (
                      <>
                        <button onClick={() => navigate('/platform-invoices')} className="menu-item">
                          <span className="menu-icon">📄</span>
                          {t('Platform Invoices')}
                        </button>
                        <button onClick={() => setShowConvertToCustomerModal(true)} className="menu-item">
                          <span className="menu-icon">🔄</span>
                          {t('Convert To Customer')}
                        </button>
                        <button onClick={handleSettingsClick} className="menu-item">
                          <span className="menu-icon">⚙️</span>
                          {t('Settings')}
                        </button>
                      </>
                    )}
                    <button onClick={async () => {
                      setShowUserMenu(false);
                      setIsMobileMenuOpen(false);
                      if (window.confirm(t('This will clear all cached data and refresh the app. Continue?'))) {
                        await forceRefreshApp();
                      }
                    }} className="menu-item">
                      <span className="menu-icon">🔄</span>
                      {t('Force Refresh App')}
                    </button>
                    <div className="menu-divider"></div>
                    <button onClick={handleLogout} className="menu-item logout">
                      <span className="menu-icon">🚪</span>
                      {t('Logout')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div ref={mobileMenuRef} className={`mobile-nav ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-nav-content">
            {/* Mobile Navigation Links */}
            <ul className="mobile-nav-links">
              <li>
                <Link to="/dashboard" className={isActive('/dashboard')}>
                  <span className="nav-icon">📊</span>
                  <span className="nav-text">{t('Dashboard')}</span>
                </Link>
              </li>
              {!user.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER') && (
                <>
                  {!user.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                    <li>
                      <Link to="/hierarchy" className={isActive('/hierarchy')}>
                        <span className="nav-icon">🌐</span>
                        <span className="nav-text">{t('Network')}</span>
                      </Link>
                    </li>
                  )}
                  {user.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                    <>
                      <li>
                        <Link to="/leads" className={isActive('/leads')}>
                          <span className="nav-icon">📋</span>
                          <span className="nav-text">{t('Leads')}</span>
                        </Link>
                      </li>
                      <li>
                        <Link to="/learning-center" className={isActive('/learning-center')}>
                          <span className="nav-icon">📚</span>
                          <span className="nav-text">{t('Learning Center')}</span>
                        </Link>
                      </li>
                      <li>
                        <Link to="/platform-tour" className={isActive('/platform-tour')}>
                          <span className="nav-icon">🎥</span>
                          <span className="nav-text">{t('Platform Tour')}</span>
                        </Link>
                      </li>
                    </>
                  )}
                  <li>
                    <Link to="/about-us" className={isActive('/about-us')}>
                      <span className="nav-icon">ℹ️</span>
                      <span className="nav-text">{t('About Us')}</span>
                    </Link>
                  </li>
                </>
              )}
              {user.systemRoles?.some(role => role.code === 'SYSTEM_CUSTOMER') && (
                <li>
                  <Link to="/platform-tour" className={isActive('/platform-tour')}>
                    <span className="nav-icon">🎥</span>
                    <span className="nav-text">{t('Platform Tour')}</span>
                  </Link>
                </li>
              )}
              <li className={`mobile-submenu ${showLanguageMenu ? 'open' : ''}`}>
                <button
                  type="button"
                  className="mobile-submenu-trigger"
                  onClick={() => setShowLanguageMenu((prev) => !prev)}
                  aria-expanded={showLanguageMenu}
                >
                  <span className="nav-icon">🌐</span>
                  <span className="nav-text">{t('Language')}</span>
                  <span className="mobile-submenu-value">{selectedLanguageLabel}</span>
                  <span className="mobile-submenu-arrow">▼</span>
                </button>
                {showLanguageMenu && (
                  <div className="mobile-submenu-items">
                    {languageOptions.map((option) => (
                      <button
                        key={option.code}
                        type="button"
                        className={`mobile-submenu-item ${language === option.code ? 'selected' : ''}`}
                        onMouseDown={(event) => handleLanguageSelect(event, option.code)}
                        onClick={(event) => handleLanguageSelect(event, option.code)}
                      >
                        <span className="menu-icon">{language === option.code ? '✓' : '•'}</span>
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </li>
            </ul>

            {/* Mobile User Actions */}
            <div className="mobile-user-actions">
              <div className="mobile-user-info">
                <span className="user-avatar">👤</span>
                <span className="user-name">{user.firstName || user.email}</span>
              </div>
              
              <div className="mobile-action-buttons">
                <button onClick={handleNotificationsClick} className="mobile-action-btn">
                  <span className="user-avatar">🔔</span>
                  <span>{t('Notifications')}</span>
                  {unreadCount > 0 && (
                    <span className="notification-badge">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                
                <button onClick={handleProfileClick} className="mobile-action-btn">
                  <span className="menu-icon">👤</span>
                  <span>{t('My Profile')}</span>
                </button>
                
                {isCustomerOnly && (
                  <>
                    <button onClick={() => navigate('/attendance/scan')} className="mobile-action-btn">
                      <span className="menu-icon">📋</span>
                      <span>{t('Scan Attendance QR')}</span>
                    </button>
                    <button onClick={handleAndroidAppDownload} className="mobile-action-btn">
                      <span className="menu-icon">📱</span>
                      <span>{t('Android App')}</span>
                    </button>
                    <button onClick={() => handleSettingsClick('attendance')} className="mobile-action-btn">
                      <span className="menu-icon">⚙️</span>
                      <span>{t('Settings')}</span>
                    </button>
                  </>
                )}
                
                {isEditor && (
                  <>
                    <button onClick={() => navigate('/platform-invoices')} className="mobile-action-btn">
                      <span className="menu-icon">📄</span>
                      <span>{t('Platform Invoices')}</span>
                    </button>
                    <button onClick={() => setShowConvertToCustomerModal(true)} className="mobile-action-btn">
                      <span className="menu-icon">🔄</span>
                      <span>{t('Convert To Customer')}</span>
                    </button>
                    <button onClick={handleSettingsClick} className="mobile-action-btn">
                      <span className="menu-icon">⚙️</span>
                      <span>{t('Settings')}</span>
                    </button>
                  </>
                )}
                
                <button onClick={async () => {
                  setIsMobileMenuOpen(false);
                  if (window.confirm(t('This will clear all cached data and refresh the app. Continue?'))) {
                    await forceRefreshApp();
                  }
                }} className="mobile-action-btn">
                  <span className="menu-icon">🔄</span>
                  <span>{t('Force Refresh App')}</span>
                </button>
                
                <button onClick={handleLogout} className="mobile-action-btn logout">
                  <span className="menu-icon">🚪</span>
                  <span>{t('Logout')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {(appDownloadLoading || appDownloadMessage) && (
        <div
          style={{
            position: 'fixed',
            top: '72px',
            right: '16px',
            zIndex: 1200,
            minWidth: '260px',
            maxWidth: '340px',
            background: 'var(--white)',
            border: '1px solid var(--gray-medium)',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            padding: '12px 14px'
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--dark-blue)', marginBottom: '6px' }}>
            {t('Android App')}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--gray-dark)', marginBottom: appDownloadLoading ? '10px' : 0 }}>
            {appDownloadMessage}
          </div>
          {appDownloadLoading && (
            <div style={{ width: '100%', height: '8px', background: 'var(--gray-light)', borderRadius: '999px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${Math.max(appDownloadProgress, 8)}%`,
                  height: '100%',
                  background: 'var(--primary-blue)',
                  transition: 'width 0.2s ease'
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal 
          user={user} 
          onClose={() => setShowProfileModal(false)} 
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <SettingsModal 
          user={user} 
          onClose={() => {
            setShowSettingsModal(false);
            setSettingsInitialTab(undefined);
          }}
          initialTab={settingsInitialTab}
        />
      )}

      {/* Club Change Modal */}
      {showClubChangeModal && (
        <ClubChangeModal 
          user={user} 
          onClose={() => setShowClubChangeModal(false)} 
        />
      )}

      {/* Convert To Customer Modal */}
      {showConvertToCustomerModal && (
        <ConvertToCustomerModal 
          user={user} 
          onClose={() => setShowConvertToCustomerModal(false)} 
        />
      )}

      {/* Club Change Modal */}
      {showClubChangeModal && (
        <ClubChangeModal 
          user={user} 
          onClose={() => setShowClubChangeModal(false)} 
        />
      )}
    </>
  );
};

export default Navbar;
